import { Waypoints, Wifi } from 'lucide-react'
import { useKioskFreebox, useKioskMikrotik } from '@/lib/kiosk'
import { Chip, Dot, Panel } from '@/components/kiosk/kioskUi'
import { C, fmt, tempColor } from '@/components/kiosk/kioskTheme'

function mbps(bytesPerS: number | null | undefined): string {
  return bytesPerS == null ? '—' : fmt((bytesPerS * 8) / 1e6, 0)
}

export function NetworkTile({ onTap }: { onTap: () => void }) {
  const { data: fb } = useKioskFreebox()
  const { data: mk } = useKioskMikrotik()
  const box = fb?.boxes?.[0]
  const wanUp = box?.wan_state === 'up' && !box?.backup_4g
  const ports = (mk?.devices ?? [])
    .flatMap((d) => d.ports.map((p) => ({ device: d.device, ...p })))
    .filter((p) => p.sfp_temp_c != null)
    .sort((a, b) => (b.sfp_temp_c ?? 0) - (a.sfp_temp_c ?? 0))
    .slice(0, 4)
  return (
    <Panel
      title="Réseau / SFP"
      icon={<Waypoints className="h-3.5 w-3.5" />}
      accent={wanUp ? C.green : C.red}
      onTap={onTap}
      right={<Dot color={wanUp ? C.green : C.red} pulse={!wanUp} />}
    >
      <div className="flex h-full flex-col gap-2">
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5" style={{ background: C.panelHi }}>
          <Wifi className="h-4 w-4" style={{ color: wanUp ? C.green : C.red }} />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold" style={{ color: C.ink }}>
              {box?.backup_4g ? 'Backup 4G' : wanUp ? 'WAN Fibre' : (box?.wan_state ?? 'inconnu')}
            </div>
            <div className="kmono text-[10px]" style={{ color: C.dim }}>
              ↓ {mbps(box?.rate_down_bytes_s)} · ↑ {mbps(box?.rate_up_bytes_s)} Mb/s
            </div>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-1">
          {ports.map((p) => (
            <div key={`${p.device}-${p.port}`} className="flex items-center gap-2 text-[12px]">
              <Dot color={p.link ? C.green : C.faint} />
              <span className="truncate" style={{ color: C.ink }}>{p.neighbor?.name ?? p.port}</span>
              <span className="truncate text-[10px]" style={{ color: C.dim }}>{p.device}</span>
              <span className="ml-auto"><Chip color={tempColor(p.sfp_temp_c)}>{Math.round(p.sfp_temp_c ?? 0)}°C</Chip></span>
            </div>
          ))}
          {ports.length === 0 && <div className="flex h-full items-center justify-center text-sm" style={{ color: C.faint }}>Pas de SFP</div>}
        </div>
      </div>
    </Panel>
  )
}

export function NetworkDetail() {
  const { data: fb } = useKioskFreebox()
  const { data: mk } = useKioskMikrotik()
  const box = fb?.boxes?.[0]
  const wanUp = box?.wan_state === 'up' && !box?.backup_4g
  const devices = mk?.devices ?? []
  return (
    <div className="grid h-full grid-cols-[300px_1fr] gap-4">
      <div className="flex flex-col gap-3">
        <div className="kcard rounded-xl border p-4" style={{ background: C.panel, borderColor: C.line }}>
          <div className="flex items-center gap-2">
            <Wifi className="h-5 w-5" style={{ color: wanUp ? C.green : C.red }} />
            <span className="text-lg font-bold" style={{ color: C.ink }}>
              {box?.backup_4g ? 'Backup 4G' : wanUp ? 'WAN Fibre' : (box?.wan_state ?? '—')}
            </span>
            <span className="ml-auto"><Dot color={wanUp ? C.green : C.red} pulse={!wanUp} /></span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="kmono text-2xl font-bold" style={{ color: C.cyan }}>{mbps(box?.rate_down_bytes_s)}</div>
              <div className="text-[10px] uppercase" style={{ color: C.dim }}>↓ Mb/s</div>
            </div>
            <div>
              <div className="kmono text-2xl font-bold" style={{ color: C.ink }}>{mbps(box?.rate_up_bytes_s)}</div>
              <div className="text-[10px] uppercase" style={{ color: C.dim }}>↑ Mb/s</div>
            </div>
          </div>
          {box?.sfp_pwr_rx_dbm != null && (
            <div className="mt-3 text-center text-[11px]" style={{ color: C.dim }}>
              SFP Freebox Rx <span className="kmono" style={{ color: C.ink }}>{box.sfp_pwr_rx_dbm.toFixed(1)} dBm</span>
            </div>
          )}
        </div>
      </div>
      <div className="grid min-h-0 grid-cols-2 gap-4">
        {devices.map((d) => (
          <div key={d.device} className="kcard flex min-h-0 flex-col rounded-xl border p-3" style={{ background: C.panel, borderColor: C.line }}>
            <div className="mb-2 flex items-center gap-2">
              <Dot color={d.reachable ? C.green : C.red} pulse={!d.reachable} />
              <span className="font-bold" style={{ color: C.ink }}>{d.device}</span>
              {d.temp_board_c != null && <span className="ml-auto"><Chip color={tempColor(d.temp_board_c)}>carte {Math.round(d.temp_board_c)}°C</Chip></span>}
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-hidden">
              {d.ports.filter((p) => p.link || p.sfp_temp_c != null).map((p) => (
                <div key={p.port} className="flex items-center gap-2 text-[12px]">
                  <Dot color={p.link ? C.green : C.faint} />
                  <span className="truncate" style={{ color: C.ink }}>{p.neighbor?.name ?? p.port}</span>
                  {p.sfp_temp_c != null && <span className="ml-auto kmono" style={{ color: tempColor(p.sfp_temp_c) }}>{Math.round(p.sfp_temp_c)}°C</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
